import { useState, type ReactNode } from 'react'
import { useParams, Link } from 'react-router'
import { courses, type CourseOutcomes } from '@/lib/courses'
import type { PublicClassOutline } from '@/lib/lms/curriculum/syllabus'
import { lessonKey } from '@/lib/portal/curriculum'
import { getPriorityCourseSeo, type CourseSeoContent } from '@/lib/courseSeo'
import { buildWhatsAppUrl, siteConfig } from '@/lib/siteConfig'
import { BreadcrumbStructuredData, CourseStructuredData, FAQStructuredData } from '@/lib/seo'
import { getEnrollment } from '@/lib/intake'
import { useCurrentTime } from '@/lib/hydration'
import { getCourseSavings } from '@/lib/pricing'
import { cn } from '@/lib/utils'
import { ToolLogoStack } from '@/components/ToolLogo'
import { SiteFooter } from '@/components/layout/SiteFooter'
import { SiteHeader } from '@/components/layout/SiteHeader'
import { EnrollmentForm } from '@/components/EnrollmentForm'
import { buttonClassName } from '@/components/ui/buttonStyles'
import { HandArrow, HandNote } from '@/components/design/HandDrawn'
import { Aurora } from '@/components/design/Aurora'
import { Reveal } from '@/components/design/Reveal'
import { SectionNav, type SectionLink } from '@/components/design/SectionNav'
import { TrustNotes } from '@/components/design/TrustNotes'
import { NotFound } from '@/pages/NotFound'
import {
  IconArrowRight,
  IconBook,
  IconBriefcase,
  IconBuilding,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconGlobe,
  IconGraduationCap,
  IconLayers,
  IconLightbulb,
  IconRocket,
  IconShield,
  IconStar,
  IconTerminal,
  IconTrendingUp,
  IconUser,
  IconUsers,
  IconX,
  IconZap,
} from '@/components/Icons'

const CARD = 'rounded-2xl border border-[var(--surface-border)] bg-[var(--surface-card)]'

function DetailSection({
  id,
  eyebrow,
  title,
  icon,
  aside,
  children,
}: {
  id: string
  eyebrow: string
  title: string
  icon: ReactNode
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-36 pb-14">
      <Reveal>
        <div className="mb-6 flex items-center gap-3">
          <span className="ml-1.5 flex size-10 shrink-0 rotate-45 items-center justify-center rounded-lg border border-[var(--surface-border-strong)] bg-[var(--surface-raised)]">
            <span className="-rotate-45 text-[var(--color-brand-accent-readable)]">{icon}</span>
          </span>
          <div className="min-w-0">
            <p className="font-code text-[10px] uppercase tracking-[0.2em] text-[var(--surface-muted)]">
              {eyebrow}
            </p>
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h2
                id={`${id}-heading`}
                className="font-display text-2xl font-bold tracking-[-0.02em] text-[var(--text-strong)]"
              >
                {title}
              </h2>
              {aside}
            </div>
          </div>
        </div>
      </Reveal>
      <Reveal delay={60}>{children}</Reveal>
    </section>
  )
}

export function CourseDetail({ classes = {} }: { classes?: Record<string, PublicClassOutline> }) {
  const now = useCurrentTime()
  const { slug } = useParams<{ slug: string }>()
  const course = courses.find((c) => c.slug === slug)

  if (!course) {
    return <NotFound />
  }

  const isOpen = course.status === 'open'
  const enrollment = getEnrollment(now)
  const whatsappUrl = buildWhatsAppUrl(
    `I want counselling for ${course.title} — ${enrollment.batchName}`,
  )
  const relatedCourses = courses
    .filter((item) => item.slug !== course.slug && item.category === course.category)
    .slice(0, 3)
  const courseSeo = getPriorityCourseSeo(course.slug)
  const promoPrice = course.earlyBirdPrice || course.discountedPrice
  const promoPriceLabel =
    course.promoPriceLabel ?? (course.earlyBirdPrice ? 'Early Bird' : 'Discount')
  const savings = getCourseSavings(course)
  const hasInstructorBio = course.instructor === 'Minhaj Sadik'

  const faqItems = courseSeo?.faqs ?? [
    {
      question: 'Can I join as a beginner? | আমি কি beginner হলে জয়েন করতে পারবো?',
      answer:
        'Yes. If you meet the listed prerequisites, you can join and grow with a step-by-step roadmap and mentor guidance. হ্যাঁ, প্রি-রিকুইজিট মিললে roadmap ও mentor guidance দিয়ে আপনি confidently শুরু করতে পারবেন।',
    },
    {
      question: 'Is installment payment available? | কিস্তিতে পেমেন্ট করা যাবে?',
      answer: isOpen
        ? 'Yes, installment options are available for most open batches. হ্যাঁ, বেশিরভাগ ওপেন ব্যাচে কিস্তি সুবিধা রয়েছে, যাতে আপনার শেখা থেমে না যায়।'
        : 'Installment options are confirmed when this course opens for admission. এই কোর্স ভর্তির জন্য খুললে কিস্তির বিস্তারিত জানানো হবে।',
    },
    {
      question: 'Will I build practical projects? | রিয়েল প্রজেক্টে কাজ করার সুযোগ থাকবে?',
      answer:
        'Open batches include practical assignments aimed at a portfolio demo. Coming-soon courses will publish their project list before admissions open. ওপেন ব্যাচে প্র্যাকটিক্যাল অ্যাসাইনমেন্ট থাকে; কামিং সুন কোর্সের প্রজেক্ট তালিকা ভর্তির আগে জানানো হবে।',
    },
  ]

  const sections: SectionLink[] = [
    ...(courseSeo ? [{ id: 'overview', label: 'Overview' }] : []),
    { id: 'included', label: "What's included" },
    { id: 'pros-cons', label: 'Pros & cons' },
    { id: 'career-paths', label: 'Career paths' },
    ...(course.outcomes ? [{ id: 'outcomes', label: 'After learning' }] : []),
    { id: 'curriculum', label: 'Curriculum' },
    { id: 'tools', label: 'Tools' },
    { id: 'audience', label: 'Who it is for' },
    { id: 'prerequisites', label: 'Prerequisites' },
    { id: 'questions', label: 'Questions' },
    ...(hasInstructorBio ? [{ id: 'instructor-bio', label: 'Instructor' }] : []),
  ]

  const facts = [
    { icon: IconCalendar, label: 'Duration', value: course.duration },
    { icon: IconClock, label: 'Schedule', value: course.schedule },
    { icon: IconUsers, label: 'Batch size', value: course.batchSize },
    { icon: IconGlobe, label: 'Language', value: course.language },
    { icon: IconUser, label: 'Instructor', value: course.instructor },
  ]

  return (
    <>
      <CourseStructuredData
        name={course.title}
        description={course.description}
        urlPath={`/courses/${course.slug}`}
        providerName={siteConfig.brand.academyName}
        price={promoPrice || course.price}
        category={course.category}
        level={course.level}
        language={course.language}
        instructor={course.instructor}
        duration={course.duration}
        schedule={course.schedule}
        batchSize={course.batchSize}
        status={course.status}
        startDate={isOpen ? enrollment.startIso : undefined}
      />
      <FAQStructuredData items={faqItems} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: '/' },
          { name: 'Courses', url: '/courses' },
          { name: course.title, url: `/courses/${course.slug}` },
        ]}
      />
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden pb-12 pt-28 sm:pt-32">
          <Aurora tone="hero" />
          <div className="relative mx-auto grid max-w-[1200px] gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_360px] lg:items-start">
            <div>
              <nav aria-label="Breadcrumb" className="animate-rise">
                <ol className="flex flex-wrap items-center gap-2 font-code text-[11px] uppercase tracking-[0.18em] text-[var(--surface-muted)]">
                  <li>
                    <Link to="/" className="no-underline hover:text-[var(--text-strong)]">
                      Home
                    </Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li>
                    <Link to="/courses" className="no-underline hover:text-[var(--text-strong)]">
                      Courses
                    </Link>
                  </li>
                  <li aria-hidden="true">/</li>
                  <li
                    aria-current="page"
                    className="max-w-[16rem] truncate text-[var(--color-brand-accent-readable)]"
                  >
                    {course.category}
                  </li>
                </ol>
              </nav>
              <div className="animate-rise mt-6 flex flex-wrap items-center gap-2 [--rise-delay:40ms]">
                {course.flagship && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-amber-200 ring-1 ring-inset ring-amber-400/25">
                    <IconStar size={12} aria-hidden="true" /> Flagship Program
                  </span>
                )}
                {isOpen ? (
                  <span className="inline-flex items-center gap-2 rounded-full bg-green-400/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-green-200 ring-1 ring-inset ring-green-400/25">
                    <span className="relative flex size-2">
                      <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-60" />
                      <span className="relative inline-flex size-2 rounded-full bg-green-400" />
                    </span>
                    Admissions Open
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-brand-accent)]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-brand-accent-readable)] ring-1 ring-inset ring-[var(--color-brand-accent)]/25">
                    Coming Soon
                  </span>
                )}
                <span className="rounded-full bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-[var(--surface-muted)] ring-1 ring-inset ring-white/10">
                  {course.level}
                </span>
              </div>
              <h1 className="animate-rise-soft mt-6 max-w-[820px] font-display text-[clamp(32px,4.8vw,56px)] font-semibold leading-[1.04] tracking-[-0.045em] text-gradient-white [--rise-delay:80ms]">
                {course.title}
              </h1>
              <p className="animate-rise mt-5 max-w-[720px] text-lg leading-relaxed text-[var(--surface-muted)] [--rise-delay:140ms]">
                {course.description}
              </p>
              <div className="animate-rise mt-8 flex flex-wrap gap-3 [--rise-delay:200ms]">
                {isOpen ? (
                  <>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        buttonClassName({ variant: 'accent', size: 'lg' }),
                        'group rounded-full px-7',
                      )}
                    >
                      Ask on WhatsApp | হোয়াটসঅ্যাপে জিজ্ঞাসা করুন
                      <IconArrowRight
                        size={16}
                        aria-hidden="true"
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </a>
                    <a
                      href="#apply"
                      className="inline-flex min-h-14 items-center rounded-full border border-white/15 bg-white/[0.04] px-6 text-sm font-semibold text-[var(--text-strong)] no-underline transition-colors hover:border-[var(--color-brand-accent)]/60 hover:bg-[var(--color-brand-accent)]/10"
                    >
                      Request counselling
                    </a>
                  </>
                ) : (
                  <a
                    href="#curriculum"
                    className="inline-flex min-h-14 items-center rounded-full border border-white/15 bg-white/[0.04] px-6 text-sm font-semibold text-[var(--text-strong)] no-underline transition-colors hover:border-[var(--color-brand-accent)]/60"
                  >
                    See the planned curriculum
                  </a>
                )}
              </div>
              {isOpen && (
                <TrustNotes
                  className="animate-rise mt-5 [--rise-delay:240ms]"
                  items={[
                    'Free counselling first',
                    'Installment support',
                    'Seat confirmed only after payment',
                  ]}
                />
              )}
              {course.outcomes && (
                <div className="animate-rise mt-8 flex max-w-[720px] flex-wrap items-end gap-x-6 gap-y-3 [--rise-delay:280ms]">
                  <div className="flex items-start gap-3 rounded-2xl border border-green-400/25 bg-green-400/[0.07] px-4 py-3">
                    <IconRocket
                      size={16}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-green-300"
                    />
                    <div>
                      <span className="block font-code text-[10px] font-semibold uppercase tracking-[0.18em] text-green-300">
                        Portfolio Milestone
                      </span>
                      <span className="text-sm leading-relaxed text-[var(--surface-fg)]">
                        {course.outcomes.firstMilestone}
                      </span>
                    </div>
                  </div>
                  <div className="hidden items-end gap-1 sm:flex">
                    <HandArrow
                      variant="curve-up-right"
                      draw="load"
                      delay={1000}
                      className="h-10 w-12 -scale-x-100 text-[var(--color-brand-accent-readable)]"
                    />
                    <HandNote className="text-xl">your first thing to show</HandNote>
                  </div>
                </div>
              )}
            </div>

            <aside
              aria-label="Price and admission"
              className="animate-rise-soft relative [--rise-delay:160ms]"
            >
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-[2.5rem] bg-[radial-gradient(circle,rgba(0,229,255,0.16),transparent_65%)] blur-2xl"
              />
              <div className="relative overflow-hidden rounded-3xl border border-[var(--color-brand-accent)]/35 bg-[rgba(13,15,18,0.92)] p-7 shadow-[0_24px_70px_-30px_rgba(0,229,255,0.45)] backdrop-blur">
                <p className="font-code text-[11px] uppercase tracking-[0.2em] text-[var(--surface-muted)]">
                  {isOpen ? `${enrollment.batchName} fee` : 'Planned fee'}
                </p>
                <div className="mt-3 font-display text-5xl font-bold tracking-[-0.03em] text-[var(--text-strong)]">
                  {promoPrice || course.price}
                </div>
                {promoPrice && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[var(--surface-muted)]">
                    <span className="line-through">{course.price}</span>
                    <span className="rounded-full bg-green-400/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase text-green-200 ring-1 ring-inset ring-green-400/25">
                      {promoPriceLabel}
                    </span>
                    {savings && (
                      <span className="w-full text-xs font-semibold text-green-300">
                        {savings.shortLabel} from regular price
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-4 text-sm text-[var(--surface-muted)]">
                  {course.duration} · {course.schedule}
                </div>
                <div className="mt-6 border-t border-[var(--surface-border)] pt-5">
                  {isOpen ? (
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        buttonClassName({ variant: 'primary', size: 'lg', fullWidth: true }),
                        'rounded-full text-sm',
                      )}
                    >
                      Ask on WhatsApp
                      <IconArrowRight size={15} aria-hidden="true" />
                    </a>
                  ) : (
                    <div className="flex min-h-14 w-full items-center justify-center rounded-full border border-[var(--surface-border)] bg-[var(--surface-raised)] text-[15px] font-semibold text-[var(--surface-muted)]">
                      Coming Soon
                    </div>
                  )}
                  <p className="mt-3 text-center text-sm text-[var(--surface-muted)]">
                    {isOpen
                      ? `Next batch starts ${enrollment.nextBatchDate} | পরবর্তী ব্যাচ শুরু`
                      : 'Dates to be announced'}
                  </p>
                </div>
              </div>
            </aside>
          </div>

          <div className="relative mx-auto mt-12 max-w-[1200px] px-4 sm:px-6">
            <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[var(--surface-border)] bg-[var(--surface-border)] sm:grid-cols-3 lg:grid-cols-5">
              {facts.map((fact) => (
                <li
                  key={fact.label}
                  className="flex flex-col items-start gap-3 bg-[var(--surface-card)] px-5 py-5"
                >
                  <span className="flex size-8 rotate-45 items-center justify-center rounded-md border border-[var(--surface-border-strong)] bg-[var(--surface-raised)]">
                    <fact.icon
                      size={14}
                      aria-hidden="true"
                      className="-rotate-45 text-[var(--color-brand-accent-readable)]"
                    />
                  </span>
                  <div>
                    <p className="font-code text-[10px] uppercase tracking-[0.18em] text-[var(--surface-muted)]">
                      {fact.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-[var(--text-strong)]">
                      {fact.value}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <SectionNav sections={sections} />

        <section className="py-14">
          <div className="mx-auto grid max-w-[1200px] gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_360px]">
            <div className="min-w-0">
              {courseSeo && <CourseSeoOverview content={courseSeo} />}

              <DetailSection
                id="included"
                eyebrow="Inside the batch"
                title="What's included"
                icon={<IconCheck size={16} aria-hidden="true" />}
              >
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {course.highlights.map((highlight) => (
                    <li
                      key={highlight}
                      className={cn(
                        CARD,
                        'flex items-start gap-3 p-4 text-sm text-[var(--surface-fg)]',
                      )}
                    >
                      <IconCheck
                        size={14}
                        aria-hidden="true"
                        className="mt-0.5 shrink-0 text-[var(--color-brand-accent-readable)]"
                      />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </DetailSection>

              <DetailSection
                id="pros-cons"
                eyebrow="Decide with open eyes"
                title="Pros and cons (সুবিধা ও বিবেচ্য বিষয়)"
                icon={<IconLayers size={16} aria-hidden="true" />}
              >
                <div className="mb-5 text-sm leading-relaxed text-[var(--surface-muted)]">
                  ভর্তির আগে এই কোর্সের সুবিধা ও কিছু গুরুত্বপূর্ণ বিষয় দেখুন — যাতে আপনি নিজেই
                  বুঝতে পারেন এই কোর্সটি আপনার জন্য সঠিক কিনা।
                  <span className="mt-1 block text-xs">
                    Review the advantages and key things to know about this course before you decide
                    to enroll.
                  </span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-green-400/25 bg-green-400/[0.05] p-5">
                    <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-green-200">
                      <IconCheck size={14} aria-hidden="true" className="shrink-0" />
                      কেন এই কোর্সে ভর্তি হবেন (Advantages)
                    </p>
                    <ul className="space-y-3">
                      {course.pros.map((pro) => (
                        <li
                          key={pro}
                          className="flex items-start gap-2.5 text-sm text-[var(--surface-fg)]"
                        >
                          <IconCheck
                            size={12}
                            aria-hidden="true"
                            className="mt-1 shrink-0 text-green-400"
                          />
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.05] p-5">
                    <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-amber-200">
                      <IconX size={14} aria-hidden="true" className="shrink-0" />
                      ভর্তির আগে যা জেনে রাখবেন (Things to Know First)
                    </p>
                    <ul className="space-y-3">
                      {course.cons.map((con) => (
                        <li
                          key={con}
                          className="flex items-start gap-2.5 text-sm text-[var(--surface-fg)]"
                        >
                          <IconX
                            size={12}
                            aria-hidden="true"
                            className="mt-1 shrink-0 text-amber-300"
                          />
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </DetailSection>

              <DetailSection
                id="career-paths"
                eyebrow="Where it leads"
                title="Career paths"
                icon={<IconTrendingUp size={16} aria-hidden="true" />}
              >
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {course.careerPaths.map((path) => (
                    <li
                      key={path}
                      className={cn(
                        CARD,
                        'flex items-center gap-3 p-4 text-sm text-[var(--surface-fg)]',
                      )}
                    >
                      <IconArrowRight
                        size={12}
                        aria-hidden="true"
                        className="shrink-0 text-[var(--color-brand-accent-readable)]"
                      />
                      <span>{path}</span>
                    </li>
                  ))}
                </ul>
              </DetailSection>

              {course.outcomes && (
                <>
                  <OutcomesSection outcomes={course.outcomes} />
                  {!courseSeo && <IncomeDisclaimer />}
                </>
              )}

              <DetailSection
                id="curriculum"
                eyebrow="Module by module"
                title="Curriculum"
                icon={<IconBook size={16} aria-hidden="true" />}
                aside={
                  <span className="text-xs text-[var(--surface-muted)]">
                    {course.curriculum.length} modules
                  </span>
                }
              >
                <div className="space-y-2.5">
                  {course.curriculum.map((module, index) => (
                    <CurriculumAccordion
                      key={`${index}-${module.title}`}
                      module={module}
                      index={index}
                      classes={classes}
                      firstClassNumber={
                        course.curriculum
                          .slice(0, index)
                          .reduce((sum, earlier) => sum + earlier.lessons.length, 0) + 1
                      }
                      defaultOpen={index === 0}
                    />
                  ))}
                </div>
              </DetailSection>

              <DetailSection
                id="tools"
                eyebrow="Your toolkit"
                title="Tools and technologies"
                icon={<IconTerminal size={16} aria-hidden="true" />}
              >
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {course.tools.map((tool) => (
                    <li
                      key={tool}
                      className="flex min-h-14 items-center gap-3.5 rounded-xl border border-[var(--surface-border)] bg-[var(--surface-card)] px-4 py-3 text-sm text-[var(--surface-fg)] transition-colors hover:border-[var(--color-brand-accent)]/40"
                    >
                      <ToolLogoStack
                        tool={tool}
                        size={18}
                        className="min-w-[44px] gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.03] px-2 py-1.5"
                      />
                      <span className="leading-snug">{tool}</span>
                    </li>
                  ))}
                </ul>
              </DetailSection>

              <DetailSection
                id="audience"
                eyebrow="Is it for you"
                title="Who is this for?"
                icon={<IconUsers size={16} aria-hidden="true" />}
              >
                <div className={cn(CARD, 'p-5')}>
                  <p className="text-sm leading-relaxed text-[var(--surface-muted)]">
                    {course.targetStudents}
                  </p>
                </div>
              </DetailSection>

              <DetailSection
                id="prerequisites"
                eyebrow="Before you start"
                title="Prerequisites"
                icon={<IconGraduationCap size={16} aria-hidden="true" />}
              >
                <ul className="space-y-2.5">
                  {course.prerequisites.map((prerequisite) => (
                    <li
                      key={prerequisite}
                      className={cn(
                        CARD,
                        'flex items-start gap-3 p-4 text-sm text-[var(--surface-fg)]',
                      )}
                    >
                      <IconChevronRight
                        size={12}
                        aria-hidden="true"
                        className="mt-1 shrink-0 text-[var(--surface-muted)]"
                      />
                      <span>{prerequisite}</span>
                    </li>
                  ))}
                </ul>
              </DetailSection>

              <DetailSection
                id="support"
                eyebrow="Our promise"
                title="Student support promise | আপনার শেখার পাশে আমরা"
                icon={<IconShield size={16} aria-hidden="true" />}
              >
                <ul className="grid gap-3 text-sm text-[var(--surface-muted)] sm:grid-cols-3">
                  <li className={cn(CARD, 'p-4')}>
                    Live classes + personal mentor feedback | লাইভ ক্লাস + ব্যক্তিগত ফিডব্যাক
                  </li>
                  <li className={cn(CARD, 'p-4')}>
                    Project-based portfolio outcomes | প্রজেক্ট দিয়ে portfolio তৈরি
                  </li>
                  <li className={cn(CARD, 'p-4')}>
                    WhatsApp support from query to enrollment | প্রশ্ন থেকে ভর্তি পর্যন্ত WhatsApp
                    সহায়তা
                  </li>
                </ul>
              </DetailSection>

              <DetailSection
                id="questions"
                eyebrow="Before you ask"
                title="Common questions | সাধারণ প্রশ্ন"
                icon={<IconLightbulb size={16} aria-hidden="true" />}
              >
                <div className="space-y-3">
                  {faqItems.map((item) => (
                    <div key={item.question} className={cn(CARD, 'p-5')}>
                      <h3 className="text-sm font-semibold text-[var(--text-strong)]">
                        {item.question}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[var(--surface-muted)]">
                        {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </DetailSection>

              {hasInstructorBio && (
                <section
                  id="instructor-bio-section"
                  aria-labelledby="instructor-bio"
                  className="scroll-mt-36"
                >
                  <Reveal>
                    <div className={cn(CARD, 'relative overflow-hidden p-6 sm:p-8')}>
                      <p className="font-code text-[10px] uppercase tracking-[0.2em] text-[var(--surface-muted)]">
                        Your mentor
                      </p>
                      <h2
                        id="instructor-bio"
                        className="mt-2 scroll-mt-36 font-display text-2xl font-bold tracking-[-0.02em] text-[var(--text-strong)]"
                      >
                        About your instructor | আপনার মেন্টর
                      </h2>
                      <div className="mt-6 flex flex-col items-start gap-6 sm:flex-row">
                        <div className="size-24 shrink-0 overflow-hidden rounded-2xl border border-[var(--surface-border-strong)] bg-[var(--surface-raised)]">
                          <img
                            src="/images/minhajsadik.jpeg"
                            alt="Minhaj Sadik, Founder & Lead Instructor at Skillo Academy"
                            loading="lazy"
                            width={800}
                            height={800}
                            className="size-full object-cover"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-relaxed text-[var(--surface-muted)] sm:text-base">
                            <strong className="text-[var(--text-strong)]">Minhaj Sadik</strong> is a
                            senior backend &amp; DevOps engineer from Sylhet, Bangladesh, and the
                            founder of Skillo Academy. He has built distributed systems serving
                            50,000+ daily users, led cloud-cost-optimization migrations that cut
                            bills 60–75%, and ships production AI agent platforms. He teaches in
                            Bangla and English with a focus on real, ship-ready projects — not
                            theory.
                          </p>
                          <div className="mt-4 flex flex-wrap gap-4 text-sm">
                            <a
                              href="https://minhajsadik.com/about"
                              rel="noopener author"
                              className="text-[var(--color-brand-accent-readable)] underline-offset-4 hover:underline"
                            >
                              Read full bio →
                            </a>
                            <a
                              href="https://github.com/MinhajSadik"
                              rel="noopener me"
                              className="text-[var(--color-brand-accent-readable)] underline-offset-4 hover:underline"
                            >
                              GitHub
                            </a>
                            <a
                              href="https://linkedin.com/in/minhajosadik"
                              rel="noopener me"
                              className="text-[var(--color-brand-accent-readable)] underline-offset-4 hover:underline"
                            >
                              LinkedIn
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                </section>
              )}
            </div>

            <div className="h-fit lg:sticky lg:top-32">
              <div className="grid gap-5">
                <EnrollmentForm
                  courseSlug={course.slug}
                  courseTitle={course.title}
                  isOpen={isOpen}
                  batchSize={course.batchSize}
                />
                {isOpen && (
                  <div className={cn(CARD, 'p-5')}>
                    <p className="text-sm font-semibold text-[var(--text-strong)]">
                      Why Students Join This Batch | কেন শিক্ষার্থীরা এই ব্যাচে ভর্তি হয়
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-[var(--surface-muted)]">
                      <li>- {course.batchSize} for focused mentorship</li>
                      <li>- Weekly mentor feedback | সাপ্তাহিক মেন্টর ফিডব্যাক</li>
                      <li>- Installment support available | কিস্তি সুবিধা রয়েছে</li>
                    </ul>
                  </div>
                )}
                {course.priceRationale && (
                  <div className={cn(CARD, 'p-5')}>
                    <p className="text-sm font-semibold text-[var(--text-strong)]">
                      {course.priceRationale.heading}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-[var(--surface-muted)]">
                      {course.priceRationale.summary}
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-[var(--surface-muted)]">
                      {course.priceRationale.points.map((point) => (
                        <li key={point} className="flex items-start gap-2">
                          <IconCheck
                            size={14}
                            aria-hidden="true"
                            className="mt-0.5 shrink-0 text-[var(--color-brand-accent-readable)]"
                          />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <dl className={cn(CARD, 'space-y-3 p-5')}>
                  {[
                    { icon: IconCalendar, label: 'Duration', value: course.duration },
                    { icon: IconUsers, label: 'Batch size', value: course.batchSize },
                    { icon: IconGraduationCap, label: 'Level', value: course.level },
                    { icon: IconGlobe, label: 'Language', value: course.language },
                    { icon: IconUser, label: 'Instructor', value: course.instructor },
                    ...(course.supportInstructor
                      ? [
                          {
                            icon: IconUser,
                            label: 'Support Instructor',
                            value: course.supportInstructor,
                          },
                        ]
                      : []),
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <dt className="inline-flex items-center gap-2 text-[var(--surface-muted)]">
                        <item.icon size={14} aria-hidden="true" />
                        {item.label}
                      </dt>
                      <dd className="text-right font-medium text-[var(--text-strong)]">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        {relatedCourses.length > 0 && (
          <section className="pb-16" aria-labelledby="related-courses">
            <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
              <h2
                id="related-courses"
                className="mb-5 font-display text-2xl font-bold tracking-[-0.02em] text-[var(--text-strong)]"
              >
                Related Courses | সম্পর্কিত কোর্স
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {relatedCourses.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/courses/${related.slug}`}
                    className={cn(
                      CARD,
                      'group p-5 no-underline transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[var(--color-brand-accent)]/40',
                    )}
                  >
                    <div className="mb-1 text-sm font-semibold text-[var(--text-strong)] transition-colors group-hover:text-[var(--color-brand-accent)]">
                      {related.title}
                    </div>
                    <div className="text-sm text-[var(--surface-muted)]">{related.subtitle}</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </>
  )
}

function CourseSeoOverview({ content }: { content: CourseSeoContent }) {
  return (
    <DetailSection
      id="overview"
      eyebrow="Introduction"
      title="Course overview"
      icon={<IconLightbulb size={16} aria-hidden="true" />}
    >
      <div className={cn(CARD, 'p-6 sm:p-7')}>
        <p className="mb-6 text-sm leading-relaxed text-[var(--surface-muted)] sm:text-base">
          {content.introAnswer}
        </p>
        <div className="grid gap-4">
          {content.sections.map((section) => (
            <section
              key={section.heading}
              className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4"
            >
              <h3 className="mb-2 text-sm font-semibold text-[var(--text-strong)]">
                {section.heading}
              </h3>
              {section.body && (
                <p className="text-sm leading-relaxed text-[var(--surface-muted)]">
                  {section.body}
                </p>
              )}
              {section.items && (
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm leading-relaxed text-[var(--surface-muted)]"
                    >
                      <IconCheck
                        size={12}
                        aria-hidden="true"
                        className="mt-1 shrink-0 text-[var(--color-brand-accent-readable)]"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </DetailSection>
  )
}

function IncomeDisclaimer() {
  return (
    <Reveal className="mb-14">
      <div className="flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-5">
        <IconShield size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-300" />
        <p className="text-sm leading-relaxed text-[var(--surface-muted)]">
          <strong className="text-[var(--text-strong)]">Income disclaimer:</strong>{' '}
          {siteConfig.trust.incomeDisclaimer}
        </p>
      </div>
    </Reveal>
  )
}

function OutcomesSection({ outcomes }: { outcomes: CourseOutcomes }) {
  const [activeTab, setActiveTab] = useState<'jobs' | 'freelance' | 'projects'>('jobs')
  const tabs = [
    { key: 'jobs', label: 'Jobs | চাকরি', Icon: IconBriefcase },
    { key: 'freelance', label: 'Freelance (optional)', Icon: IconTrendingUp },
    { key: 'projects', label: 'Projects | প্রজেক্ট', Icon: IconRocket },
  ] as const

  return (
    <DetailSection
      id="outcomes"
      eyebrow="After learning"
      title="Possible paths after learning | শেখার পর সম্ভাব্য পথ"
      icon={<IconZap size={16} aria-hidden="true" />}
    >
      <p className="mb-5 max-w-[640px] text-sm leading-relaxed text-[var(--surface-muted)]">
        These are possible directions, not guarantees. Your results depend on practice, portfolio,
        communication, consistency, and market demand.
      </p>
      <div className="mb-5 flex w-fit flex-wrap gap-1 rounded-full border border-[var(--surface-border)] bg-[var(--surface-card)] p-1">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            aria-pressed={activeTab === key}
            onClick={() => setActiveTab(key)}
            className={cn(
              'inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-full px-4 text-xs font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent-readable)]',
              activeTab === key
                ? 'bg-[var(--color-brand-accent)]/15 text-[var(--color-brand-accent)] ring-1 ring-inset ring-[var(--color-brand-accent)]/40'
                : 'text-[var(--surface-muted)] hover:text-[var(--text-strong)]',
            )}
          >
            <Icon size={13} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'jobs' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {outcomes.jobs.map((job) => (
            <div key={job.role} className={cn(CARD, 'flex flex-col gap-3 p-5')}>
              <div>
                <p className="mb-1 text-sm font-semibold text-[var(--text-strong)]">{job.role}</p>
                <p className="inline-flex items-center gap-1.5 text-xs text-[var(--surface-muted)]">
                  <IconBuilding size={11} aria-hidden="true" className="shrink-0" />
                  {job.where}
                </p>
              </div>
              <p className="text-sm font-bold text-green-300">{job.salary}</p>
              <div className="flex items-start gap-2 border-t border-[var(--surface-border)] pt-3">
                <IconLightbulb
                  size={13}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0 text-amber-300"
                />
                <p className="text-xs leading-relaxed text-[var(--surface-muted)]">{job.tip}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'freelance' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {outcomes.freelance.map((gig) => (
            <div key={`${gig.platform}-${gig.gig}`} className={cn(CARD, 'flex flex-col gap-3 p-5')}>
              <div>
                <span className="mb-2 inline-block rounded-full bg-[var(--color-brand-accent)]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[var(--color-brand-accent-readable)] ring-1 ring-inset ring-[var(--color-brand-accent)]/25">
                  {gig.platform}
                </span>
                <p className="text-sm font-semibold text-[var(--text-strong)]">{gig.gig}</p>
              </div>
              <p className="text-sm font-bold text-green-300">{gig.earning}</p>
              <div className="flex items-start gap-2 border-t border-[var(--surface-border)] pt-3">
                <IconLightbulb
                  size={13}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0 text-amber-300"
                />
                <p className="text-xs leading-relaxed text-[var(--surface-muted)]">{gig.tip}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'projects' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {outcomes.projects.map((project) => (
            <div key={project.name} className={cn(CARD, 'flex flex-col gap-3 p-5')}>
              <div>
                <p className="mb-1 text-sm font-semibold text-[var(--text-strong)]">
                  {project.name}
                </p>
                <p className="inline-flex items-center gap-1.5 text-xs text-[var(--surface-muted)]">
                  <IconUsers size={11} aria-hidden="true" className="shrink-0" />
                  {project.client}
                </p>
              </div>
              <p className="text-sm font-bold text-green-300">{project.value}</p>
              <p className="border-t border-[var(--surface-border)] pt-3 text-xs leading-relaxed text-[var(--surface-muted)]">
                {project.description}
              </p>
            </div>
          ))}
        </div>
      )}
    </DetailSection>
  )
}

function PreviewBadge() {
  return (
    <span className="rounded-full bg-green-400/10 px-2.5 py-0.5 text-[11px] font-medium text-green-200 ring-1 ring-inset ring-green-400/25">
      Preview
    </span>
  )
}

function CurriculumAccordion({
  module,
  index,
  classes,
  firstClassNumber,
  defaultOpen,
}: {
  module: { title: string; lessons: { title: string; preview?: boolean }[] }
  index: number
  classes: Record<string, PublicClassOutline>
  firstClassNumber: number
  defaultOpen?: boolean
}) {
  const hasClassOutlines = module.lessons.some(
    (_, lessonIndex) => classes[lessonKey(index, lessonIndex)],
  )

  return (
    <details
      open={defaultOpen}
      className="group/module overflow-hidden rounded-2xl border border-[var(--surface-border)] bg-[var(--surface-card)] transition-colors duration-200 hover:border-[var(--surface-border-strong)] open:border-[var(--color-brand-accent)]/30 open:hover:border-[var(--color-brand-accent)]/30"
    >
      <summary
        id={`course-module-trigger-${index}`}
        className="flex cursor-pointer list-none items-center gap-4 px-5 py-4 transition-colors duration-200 hover:bg-white/[0.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-accent-readable)] [&::-webkit-details-marker]:hidden"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--surface-raised)] font-code text-[11px] font-semibold text-[var(--color-brand-accent-readable)] ring-1 ring-inset ring-[var(--surface-border-strong)] group-open/module:bg-[var(--color-brand-accent)] group-open/module:text-[var(--accent-ink)] group-open/module:ring-0">
          {(index + 1).toString().padStart(2, '0')}
        </span>
        <span className="flex-1 text-sm font-semibold text-[var(--text-strong)]">
          {module.title}
        </span>
        <span className="flex items-center gap-2">
          <span className="hidden text-xs text-[var(--surface-muted)] sm:inline">
            {module.lessons.length} {hasClassOutlines ? 'classes' : 'lessons'}
          </span>
          <IconChevronDown
            size={15}
            aria-hidden="true"
            className="text-[var(--surface-muted)] transition-transform duration-200 group-open/module:rotate-180"
          />
        </span>
      </summary>
      <div
        id={`course-module-panel-${index}`}
        className="border-t border-[var(--surface-border)] bg-[var(--surface-raised)]/60 px-5 py-4"
      >
        <ul className="space-y-2.5">
          {module.lessons.map((lesson, lessonIndex) => {
            const outline = classes[lessonKey(index, lessonIndex)]
            if (!outline) {
              return (
                <li
                  key={`${lesson.title}-${lessonIndex}`}
                  className="flex items-center gap-3 text-sm text-[var(--surface-muted)]"
                >
                  <span className="w-6 shrink-0 font-code text-[10px] text-[var(--surface-muted)]">
                    {String(lessonIndex + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1">{lesson.title}</span>
                  {lesson.preview && <PreviewBadge />}
                </li>
              )
            }
            const classNumber = String(firstClassNumber + lessonIndex).padStart(2, '0')
            return (
              <li key={`${lesson.title}-${lessonIndex}`}>
                <details className="group/class">
                  <summary className="flex cursor-pointer list-none items-center gap-3 rounded-lg py-1 text-sm text-[var(--surface-muted)] hover:text-[var(--text-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent-readable)] [&::-webkit-details-marker]:hidden">
                    <span className="w-6 shrink-0 font-code text-[10px] text-[var(--surface-muted)]">
                      {classNumber}
                    </span>
                    <span className="flex-1">
                      <span className="sr-only">Class {classNumber}:</span> {lesson.title}
                    </span>
                    {lesson.preview && <PreviewBadge />}
                    <IconChevronDown
                      size={13}
                      aria-hidden="true"
                      className="shrink-0 transition-transform duration-200 group-open/class:rotate-180"
                    />
                  </summary>
                  <div className="mb-2 ml-9 mt-2 flex flex-col gap-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface-card)] px-4 py-3 text-sm">
                    <p className="text-[var(--surface-fg)]">{outline.objective}</p>
                    <p className="font-code text-[10px] uppercase tracking-[0.16em] text-[var(--surface-muted)]">
                      Topics
                    </p>
                    <ol className="list-decimal space-y-1 pl-5 text-[var(--surface-muted)] marker:font-code marker:text-[10px]">
                      {outline.topics.map((topic, topicIndex) => (
                        <li key={`${topicIndex}-${topic}`}>{topic}</li>
                      ))}
                    </ol>
                  </div>
                </details>
              </li>
            )
          })}
        </ul>
      </div>
    </details>
  )
}
